// SMS Service for BookAPT
import SimpleStorage from '../utils/simpleStorage';
import { Platform } from 'react-native';

// process.env.API_BASE_URL is never actually set anywhere in this app (no
// build step defines it), so every call below was silently falling back to
// localhost:3003 in production — meaning opt-out checks/syncs and SMS sends
// were talking to nothing. bookapt-sms-webhook.onrender.com is the real,
// deployed Twilio backend (backend-twilio-sms-webhook.js).
const SMS_API_BASE_URL = __DEV__
  ? 'http://localhost:3003/api'
  : 'https://bookapt-sms-webhook.onrender.com/api';

export interface SMSMessage {
  id: string;
  to: string;
  message: string;
  type: 'reminder' | 'confirmation' | 'cancellation' | 'promotion' | 'general';
  status: 'pending' | 'sent' | 'delivered' | 'failed';
  scheduledFor?: string;
  sentAt?: string;
  deliveryReport?: string;
}

export interface SMSTemplate {
  id: string;
  name: string;
  type: 'reminder' | 'confirmation' | 'cancellation' | 'promotion' | 'general';
  template: string;
  variables: string[];
  isActive: boolean;
}

class SMSService {
  private readonly SMS_MESSAGES_KEY = 'sms_messages';
  private readonly SMS_TEMPLATES_KEY = 'sms_templates';
  private readonly SMS_SETTINGS_KEY = 'sms_settings';

  // Default SMS templates
  private readonly DEFAULT_TEMPLATES: SMSTemplate[] = [
    {
      id: 'appointment_reminder',
      name: 'Appointment Reminder',
      type: 'reminder',
      template: 'Hi {customerName}! This is a reminder that you have an appointment for {service} on {date} at {time} with {businessName}. Reply STOP to opt out.',
      variables: ['customerName', 'service', 'date', 'time', 'businessName'],
      isActive: true
    },
    {
      id: 'appointment_confirmation',
      name: 'Appointment Confirmation',
      type: 'confirmation',
      template: 'Your appointment for {service} on {date} at {time} has been confirmed! We look forward to seeing you at {businessName}. Reply STOP to opt out.',
      variables: ['service', 'date', 'time', 'businessName'],
      isActive: true
    },
    {
      id: 'appointment_cancellation',
      name: 'Appointment Cancellation',
      type: 'cancellation',
      template: 'Your appointment for {service} on {date} at {time} has been cancelled. Please contact us to reschedule. Reply STOP to opt out.',
      variables: ['service', 'date', 'time'],
      isActive: true
    },
    {
      id: 'loyalty_points',
      name: 'Loyalty Points Earned',
      type: 'promotion',
      template: 'Congratulations! You earned {points} loyalty points for your {service} appointment. You now have {totalPoints} points. Reply STOP to opt out.',
      variables: ['points', 'service', 'totalPoints'],
      isActive: true
    },
    {
      id: 'promotional_offer',
      name: 'Promotional Offer',
      type: 'promotion',
      template: 'Special offer from {businessName}: {offerDescription}. Valid until {expiryDate}. Book now! Reply STOP to opt out.',
      variables: ['businessName', 'offerDescription', 'expiryDate'],
      isActive: true
    }
  ];

  constructor() {
    this.initializeTemplates();
  }

  // Initialize default templates
  private async initializeTemplates() {
    try {
      const existingTemplates = await this.getTemplates();
      if (existingTemplates.length === 0) {
        await this.saveTemplates(this.DEFAULT_TEMPLATES);
      }
    } catch (error) {
      console.error('Error initializing SMS templates:', error);
    }
  }

  // Send SMS message
  async sendSMS(to: string, message: string, type: 'reminder' | 'confirmation' | 'cancellation' | 'promotion' | 'general' = 'general'): Promise<boolean> {
    try {
      const formattedNumber = this.formatPhoneNumber(to);
      
      // ⚠️ A2P COMPLIANCE: Check opt-out status before sending
      const isOptedOut = await this.isOptedOut(formattedNumber);
      
      if (isOptedOut) {
        console.log('🚫 SMS not sent - phone number opted out:', formattedNumber);
        // Still save to history with "blocked" status for audit trail
        const smsMessage: SMSMessage = {
          id: Date.now().toString(),
          to: formattedNumber,
          message,
          type,
          status: 'blocked',
          sentAt: new Date().toISOString(),
          deliveryReport: 'Blocked - user opted out'
        };
        await this.saveMessage(smsMessage);
        return false;
      }

      const smsMessage: SMSMessage = {
        id: Date.now().toString(),
        to: formattedNumber,
        message,
        type,
        status: 'pending',
        sentAt: new Date().toISOString()
      };

      // Send via the backend (never call Twilio directly from the client —
      // that means shipping the Twilio Auth Token in the app bundle/JS, a
      // real credential exposure that was found and fixed in this pass).
      console.log('📤 Sending SMS:', {
        to: smsMessage.to,
        type: smsMessage.type,
        length: message.length,
      });

      let result: { success: boolean; messageId?: string; error?: string };
      try {
        const response = await fetch(`${SMS_API_BASE_URL}/sms/send-booking-confirmation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ to: formattedNumber, message }),
        });
        const data = await response.json();
        result = { success: !!data.success, messageId: data.sid, error: data.error };
      } catch (fetchError: any) {
        result = { success: false, error: fetchError?.message || 'Network error' };
      }

      if (result.success) {
        smsMessage.status = 'sent';
        smsMessage.deliveryReport = `Sent via Twilio - ID: ${result.messageId}`;
      } else {
        smsMessage.status = 'failed';
        smsMessage.deliveryReport = `Failed: ${result.error}`;
      }

      // Save message to history
      await this.saveMessage(smsMessage);

      return result.success;
    } catch (error) {
      console.error('Error sending SMS:', error);
      return false;
    }
  }

  // Send appointment reminder
  async sendAppointmentReminder(appointmentData: {
    customerName: string;
    customerPhone: string;
    service: string;
    date: string;
    time: string;
    businessName: string;
  }): Promise<boolean> {
    try {
      const template = await this.getTemplate('appointment_reminder');
      if (!template) {
        throw new Error('Appointment reminder template not found');
      }

      const message = this.replaceTemplateVariables(template.template, {
        customerName: appointmentData.customerName,
        service: appointmentData.service,
        date: appointmentData.date,
        time: appointmentData.time,
        businessName: appointmentData.businessName
      });

      return await this.sendSMS(appointmentData.customerPhone, message, 'reminder');
    } catch (error) {
      console.error('Error sending appointment reminder:', error);
      return false;
    }
  }

  // Send appointment confirmation
  async sendAppointmentConfirmation(appointmentData: {
    customerPhone: string;
    service: string;
    date: string;
    time: string;
    businessName: string;
  }): Promise<boolean> {
    try {
      const template = await this.getTemplate('appointment_confirmation');
      if (!template) {
        throw new Error('Appointment confirmation template not found');
      }

      const message = this.replaceTemplateVariables(template.template, {
        service: appointmentData.service,
        date: appointmentData.date,
        time: appointmentData.time,
        businessName: appointmentData.businessName
      });

      return await this.sendSMS(appointmentData.customerPhone, message, 'confirmation');
    } catch (error) {
      console.error('Error sending appointment confirmation:', error);
      return false;
    }
  }

  // Send loyalty points notification
  async sendLoyaltyPointsNotification(customerData: {
    customerPhone: string;
    points: number;
    service: string;
    totalPoints: number;
  }): Promise<boolean> {
    try {
      const template = await this.getTemplate('loyalty_points');
      if (!template) {
        throw new Error('Loyalty points template not found');
      }

      const message = this.replaceTemplateVariables(template.template, {
        points: customerData.points.toString(),
        service: customerData.service,
        totalPoints: customerData.totalPoints.toString()
      });

      return await this.sendSMS(customerData.customerPhone, message, 'promotion');
    } catch (error) {
      console.error('Error sending loyalty points notification:', error);
      return false;
    }
  }

  // Send promotional offer
  async sendPromotionalOffer(customerData: {
    customerPhone: string;
    businessName: string;
    offerDescription: string;
    expiryDate: string;
  }): Promise<boolean> {
    try {
      const template = await this.getTemplate('promotional_offer');
      if (!template) {
        throw new Error('Promotional offer template not found');
      }

      const message = this.replaceTemplateVariables(template.template, {
        businessName: customerData.businessName,
        offerDescription: customerData.offerDescription,
        expiryDate: customerData.expiryDate
      });

      return await this.sendSMS(customerData.customerPhone, message, 'promotion');
    } catch (error) {
      console.error('Error sending promotional offer:', error);
      return false;
    }
  }

  // Get SMS templates (Firestore first, then local fallback)
  async getTemplates(): Promise<SMSTemplate[]> {
    try {
      // Try to load from Firestore first (most up-to-date)
      let loadedTemplates: SMSTemplate[] = [];
      let loadedFromFirestore = false;
      
      if (Platform.OS === 'web') {
        try {
          const { getWebDb, getFirestoreFunctions } = require('./unifiedAuthService');
          const { AuthManager } = await import('../utils/authManager');
          const firestoreMod = getFirestoreFunctions?.();
          if (!firestoreMod) {
            throw new Error('Firestore module unavailable');
          }
          const { collection, getDocs, query, where, doc } = firestoreMod;
          
          // Get Firestore from the shared web Firebase instance (auto-initializes if needed)
          const firestoreDb = getWebDb();
          if (!firestoreDb) {
            console.warn('⚠️  SMSService: Firebase webDb not available, skipping Firestore load');
            throw new Error('webDb unavailable');
          }

          // Get current business owner to filter templates
          const businessOwner = await AuthManager.getSavedBusinessOwner();
          if (businessOwner) {
            // Use collection() and doc() properly for subcollections
            const businessDocRef = doc(firestoreDb, 'businesses', businessOwner.id);
            const templatesRef = collection(businessDocRef, 'sms_templates');
            const templatesQuery = query(
              templatesRef,
              where('isActive', '==', true)
            );
            const templatesSnapshot = await getDocs(templatesQuery);
            loadedTemplates = templatesSnapshot.docs.map((snap: any) => ({
              id: snap.id,
              ...snap.data()
            } as SMSTemplate));
            loadedFromFirestore = true;
            console.log(`✅ SMSService: Loaded ${loadedTemplates.length} templates from Firestore`);
            
            // Sync to local storage
            await SimpleStorage.setItem(this.SMS_TEMPLATES_KEY, JSON.stringify(loadedTemplates));
          }
        } catch (firestoreError: any) {
          console.warn('⚠️  SMSService: Could not load templates from Firestore, trying local storage:', firestoreError);
        }
      }
      
      // Fallback to local storage if Firestore didn't work
      if (!loadedFromFirestore) {
      const templatesJson = await SimpleStorage.getItem(this.SMS_TEMPLATES_KEY);
        if (templatesJson) {
          loadedTemplates = JSON.parse(templatesJson);
          console.log(`📋 SMSService: Loaded ${loadedTemplates.length} templates from local storage`);
        }
      }
      
      return loadedTemplates;
    } catch (error) {
      console.error('Error getting SMS templates:', error);
      return [];
    }
  }

  // Get specific template
  async getTemplate(templateId: string): Promise<SMSTemplate | null> {
    try {
      const templates = await this.getTemplates();
      return templates.find(t => t.id === templateId) || null;
    } catch (error) {
      console.error('Error getting SMS template:', error);
      return null;
    }
  }

  // Save templates (Firestore + local storage)
  private async saveTemplates(templates: SMSTemplate[]): Promise<void> {
    try {
      // Save to local storage (for offline access)
      await SimpleStorage.setItem(this.SMS_TEMPLATES_KEY, JSON.stringify(templates));
      
      // Also save to Firestore (for cross-browser/device access)
      if (Platform.OS === 'web') {
        try {
          const { getWebDb, getFirestoreFunctions } = require('./unifiedAuthService');
          const { AuthManager } = await import('../utils/authManager');
          const firestoreMod = getFirestoreFunctions?.();
          if (!firestoreMod) {
            console.warn('⚠️  SMSService: Firestore module unavailable, skipping Firestore save');
            return;
          }
          const { doc, setDoc, collection } = firestoreMod;
          
          // Get Firestore from the shared web Firebase instance (auto-initializes if needed)
          const firestoreDb = getWebDb();
          if (!firestoreDb) {
            console.warn('⚠️  SMSService: Firebase webDb not available, skipping Firestore save');
            return;
          }

          const businessOwner = await AuthManager.getSavedBusinessOwner();
          if (businessOwner) {
            // Use collection() and doc() properly for subcollections
            const businessDocRef = doc(firestoreDb, 'businesses', businessOwner.id);
            const templatesRef = collection(businessDocRef, 'sms_templates');
            
            // Save each template as a separate document
            for (const template of templates) {
              const templateDocRef = doc(templatesRef, template.id);
              await setDoc(templateDocRef, {
                ...template,
                updatedAt: new Date().toISOString(),
              });
            }
            console.log(`✅ SMSService: ${templates.length} templates saved to Firestore`);
          }
        } catch (firestoreError: any) {
          console.error('❌ SMSService: Error saving templates to Firestore:', firestoreError);
          console.warn('⚠️  Templates saved locally but not in Firestore');
          // Don't throw - local save succeeded
        }
      }
    } catch (error) {
      console.error('Error saving SMS templates:', error);
    }
  }

  // Get SMS messages history (Firestore first, then local fallback)
  async getMessages(): Promise<SMSMessage[]> {
    try {
      // Try to load from Firestore first (most up-to-date)
      let loadedMessages: SMSMessage[] = [];
      let loadedFromFirestore = false;
      
      if (Platform.OS === 'web') {
        try {
          const { getWebDb, getFirestoreFunctions } = require('./unifiedAuthService');
          const { AuthManager } = await import('../utils/authManager');
          const firestoreMod = getFirestoreFunctions?.();
          if (!firestoreMod) {
            throw new Error('Firestore module unavailable');
          }
          const { collection, getDocs, query, orderBy, limit } = firestoreMod;
          const db = getWebDb();
          
          if (db) {
            // Get current business owner to filter messages
            const businessOwner = await AuthManager.getSavedBusinessOwner();
            if (businessOwner) {
              const messagesQuery = query(
                collection(db, 'businesses', businessOwner.id, 'sms_messages'),
                orderBy('sentAt', 'desc'),
                limit(100) // Limit to last 100 messages for performance
              );
              const messagesSnapshot = await getDocs(messagesQuery);
              loadedMessages = messagesSnapshot.docs.map((snap: any) => ({
                id: snap.id,
                ...snap.data()
              } as SMSMessage));
              loadedFromFirestore = true;
              console.log(`✅ SMSService: Loaded ${loadedMessages.length} messages from Firestore`);
              
              // Sync to local storage (limit to last 50 for performance)
              await SimpleStorage.setItem(this.SMS_MESSAGES_KEY, JSON.stringify(loadedMessages.slice(0, 50)));
            }
          }
        } catch (firestoreError: any) {
          console.warn('⚠️  SMSService: Could not load messages from Firestore, trying local storage:', firestoreError);
        }
      }
      
      // Fallback to local storage if Firestore didn't work
      if (!loadedFromFirestore) {
      const messagesJson = await SimpleStorage.getItem(this.SMS_MESSAGES_KEY);
        if (messagesJson) {
          loadedMessages = JSON.parse(messagesJson);
          console.log(`📋 SMSService: Loaded ${loadedMessages.length} messages from local storage`);
        }
      }
      
      return loadedMessages;
    } catch (error) {
      console.error('Error getting SMS messages:', error);
      return [];
    }
  }

  // Save SMS message (Firestore + local storage)
  private async saveMessage(message: SMSMessage): Promise<void> {
    try {
      const messages = await this.getMessages();
      messages.unshift(message); // Add to beginning (most recent first)
      
      // Keep only last 50 messages in local storage for performance
      const recentMessages = messages.slice(0, 50);
      
      // Save to local storage (for offline access)
      await SimpleStorage.setItem(this.SMS_MESSAGES_KEY, JSON.stringify(recentMessages));
      
      // Also save to Firestore (for cross-browser/device access)
      if (Platform.OS === 'web') {
        try {
          const { getWebDb, getFirestoreFunctions } = require('./unifiedAuthService');
          const { AuthManager } = await import('../utils/authManager');
          const firestoreMod = getFirestoreFunctions?.();
          if (!firestoreMod) {
            console.warn('⚠️  SMSService: Firestore module unavailable, skipping Firestore save');
            return;
          }
          const { doc, setDoc } = firestoreMod;
          const db = getWebDb();
          
          if (db) {
            const businessOwner = await AuthManager.getSavedBusinessOwner();
            if (businessOwner) {
              // Generate message ID if not provided
              const messageId = message.id || `sms_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
              const messageWithId = { ...message, id: messageId };
              
              await setDoc(doc(db, 'businesses', businessOwner.id, 'sms_messages', messageId), {
                ...messageWithId,
                sentAt: messageWithId.sentAt || new Date().toISOString(),
                createdAt: messageWithId.sentAt || new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              });
              console.log(`✅ SMSService: SMS message saved to Firestore: ${messageId}`);
            }
          }
        } catch (firestoreError: any) {
          console.error('❌ SMSService: Error saving message to Firestore:', firestoreError);
          console.warn('⚠️  Message saved locally but not in Firestore');
          // Don't throw - local save succeeded
        }
      }
    } catch (error) {
      console.error('Error saving SMS message:', error);
    }
  }

  // Replace template variables
  private replaceTemplateVariables(template: string, variables: { [key: string]: string }): string {
    let message = template;
    Object.keys(variables).forEach(key => {
      const placeholder = `{${key}}`;
      message = message.replace(new RegExp(placeholder, 'g'), variables[key]);
    });
    return message;
  }

  // Format phone number
  private formatPhoneNumber(phone: string): string {
    // Remove all non-numeric characters
    const cleaned = phone.replace(/\D/g, '');
    
    // Add country code if not present (assuming US)
    if (cleaned.length === 10) {
      return `+1${cleaned}`;
    } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+${cleaned}`;
    }
    
    return phone; // Return original if can't format
  }

  // Get SMS settings (Firestore first, then local fallback)
  async getSettings(): Promise<{
    enabled: boolean;
    reminderHours: number;
    confirmationEnabled: boolean;
    promotionEnabled: boolean;
    optOutNumbers: string[];
  }> {
    try {
      // Try to load from Firestore first (most up-to-date)
      let loadedSettings: {
        enabled: boolean;
        reminderHours: number;
        confirmationEnabled: boolean;
        promotionEnabled: boolean;
        optOutNumbers: string[];
      } | null = null;
      let loadedFromFirestore = false;
      
      if (Platform.OS === 'web') {
        try {
          const { getWebDb, getFirestoreFunctions } = require('./unifiedAuthService');
          const { AuthManager } = await import('../utils/authManager');
          const firestoreMod = getFirestoreFunctions?.();
          if (!firestoreMod) {
            throw new Error('Firestore module unavailable');
          }
          const { doc, getDoc } = firestoreMod;
          const db = getWebDb();
          
          if (db) {
            const businessOwner = await AuthManager.getSavedBusinessOwner();
            if (businessOwner) {
              const settingsDoc = await getDoc(doc(db, 'businesses', businessOwner.id, 'sms_settings', 'current'));
              if (settingsDoc.exists()) {
                loadedSettings = settingsDoc.data() as any;
                loadedFromFirestore = true;
                console.log(`✅ SMSService: Loaded SMS settings from Firestore`);
                
                // Sync to local storage
                await SimpleStorage.setItem(this.SMS_SETTINGS_KEY, JSON.stringify(loadedSettings));
              }
            }
          }
        } catch (firestoreError: any) {
          console.warn('⚠️  SMSService: Could not load settings from Firestore, trying local storage:', firestoreError);
        }
      }
      
      // Fallback to local storage or defaults if Firestore didn't work
      if (!loadedFromFirestore || !loadedSettings) {
      const settingsJson = await SimpleStorage.getItem(this.SMS_SETTINGS_KEY);
        if (settingsJson) {
          loadedSettings = JSON.parse(settingsJson);
          console.log(`📋 SMSService: Loaded SMS settings from local storage`);
        } else {
          // Default settings
          loadedSettings = {
        enabled: true,
        reminderHours: 24,
        confirmationEnabled: true,
        promotionEnabled: true,
        optOutNumbers: []
      };
        }
      }
      
      return loadedSettings;
    } catch (error) {
      console.error('Error getting SMS settings:', error);
      return {
        enabled: true,
        reminderHours: 24,
        confirmationEnabled: true,
        promotionEnabled: true,
        optOutNumbers: []
      };
    }
  }

  // Update SMS settings (Firestore + local storage)
  async updateSettings(settings: {
    enabled: boolean;
    reminderHours: number;
    confirmationEnabled: boolean;
    promotionEnabled: boolean;
    optOutNumbers: string[];
  }): Promise<void> {
    try {
      // Save to local storage (for offline access)
      await SimpleStorage.setItem(this.SMS_SETTINGS_KEY, JSON.stringify(settings));
      
      // Also save to Firestore (for cross-browser/device access)
      if (Platform.OS === 'web') {
        try {
          const { getWebDb, getFirestoreFunctions } = require('./unifiedAuthService');
          const { AuthManager } = await import('../utils/authManager');
          const firestoreMod = getFirestoreFunctions?.();
          if (!firestoreMod) {
            console.warn('⚠️  SMSService: Firestore module unavailable, skipping Firestore save');
            return;
          }
          const { doc, setDoc } = firestoreMod;
          const db = getWebDb();
          
          if (db) {
            const businessOwner = await AuthManager.getSavedBusinessOwner();
            if (businessOwner) {
              await setDoc(doc(db, 'businesses', businessOwner.id, 'sms_settings', 'current'), {
                ...settings,
                updatedAt: new Date().toISOString(),
              });
              console.log(`✅ SMSService: SMS settings saved to Firestore`);
            }
          }
        } catch (firestoreError: any) {
          console.error('❌ SMSService: Error saving settings to Firestore:', firestoreError);
          console.warn('⚠️  Settings saved locally but not in Firestore');
          // Don't throw - local save succeeded
        }
      }
    } catch (error) {
      console.error('Error updating SMS settings:', error);
    }
  }

  // Check if number is opted out
  // Checks both local storage and backend API (for A2P compliance)
  async isOptedOut(phoneNumber: string): Promise<boolean> {
    try {
      const formattedNumber = this.formatPhoneNumber(phoneNumber);
      
      // Check local storage first (fast)
      const settings = await this.getSettings();
      if (settings.optOutNumbers.includes(formattedNumber)) {
        return true;
      }
      
      // Check backend API (for webhook opt-outs)
      try {
        const response = await fetch(`${SMS_API_BASE_URL}/sms/opt-out/check/${encodeURIComponent(formattedNumber)}`);
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.isOptedOut) {
            // Sync to local storage
            if (!settings.optOutNumbers.includes(formattedNumber)) {
              settings.optOutNumbers.push(formattedNumber);
              await this.updateSettings(settings);
            }
            return true;
          }
        }
      } catch (apiError) {
        // If backend is unavailable, fall back to local storage only
        console.warn('Backend opt-out check failed, using local storage only:', apiError);
      }
      
      return false;
    } catch (error) {
      console.error('Error checking opt-out status:', error);
      // On error, err on the side of caution - don't send if uncertain
      return false;
    }
  }

  // Add number to opt-out list
  // Updates both local storage and backend API (for A2P compliance)
  async optOut(phoneNumber: string): Promise<void> {
    try {
      const formattedNumber = this.formatPhoneNumber(phoneNumber);
      
      // Update local storage
      const settings = await this.getSettings();
      if (!settings.optOutNumbers.includes(formattedNumber)) {
        settings.optOutNumbers.push(formattedNumber);
        await this.updateSettings(settings);
      }
      
      // Sync to backend API
      try {
        await fetch(`${SMS_API_BASE_URL}/sms/opt-out`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ phoneNumber: formattedNumber }),
        });
      } catch (apiError) {
        console.warn('Failed to sync opt-out to backend:', apiError);
        // Continue anyway - local storage is updated
      }
    } catch (error) {
      console.error('Error opting out number:', error);
      throw error;
    }
  }
  
  // Opt in a phone number (remove from opt-out list)
  async optIn(phoneNumber: string): Promise<void> {
    try {
      const formattedNumber = this.formatPhoneNumber(phoneNumber);
      
      // Update local storage
      const settings = await this.getSettings();
      const index = settings.optOutNumbers.indexOf(formattedNumber);
      if (index > -1) {
        settings.optOutNumbers.splice(index, 1);
        await this.updateSettings(settings);
      }
      
      // Sync to backend API
      try {
        await fetch(`${SMS_API_BASE_URL}/sms/opt-in`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ phoneNumber: formattedNumber }),
        });
      } catch (apiError) {
        console.warn('Failed to sync opt-in to backend:', apiError);
        // Continue anyway - local storage is updated
      }
    } catch (error) {
      console.error('Error opting in number:', error);
      throw error;
    }
  }

  // Get SMS statistics
  async getStatistics(): Promise<{
    totalSent: number;
    totalDelivered: number;
    totalFailed: number;
    byType: { [key: string]: number };
  }> {
    try {
      const messages = await this.getMessages();
      const stats = {
        totalSent: messages.length,
        totalDelivered: messages.filter(m => m.status === 'delivered').length,
        totalFailed: messages.filter(m => m.status === 'failed').length,
        byType: {} as { [key: string]: number }
      };

      // Count by type
      messages.forEach(message => {
        stats.byType[message.type] = (stats.byType[message.type] || 0) + 1;
      });

      return stats;
    } catch (error) {
      console.error('Error getting SMS statistics:', error);
      return {
        totalSent: 0,
        totalDelivered: 0,
        totalFailed: 0,
        byType: {}
      };
    }
  }
}

export const smsService = new SMSService();

